import {CountryEmployeesView, CoverageRequirementView} from '@frontend/shared-api';
import {
  CoverageCell,
  FunctionSubtotal,
  WeightedCoverage,
} from './step-5-3-rfi-invitation-list.types';

export function weightedCoverage(
  requirements: CoverageRequirementView[],
  availableFunctionIds: number[],
  availableCountryIsoCodes: string[],
): WeightedCoverage {
  const toolFunctionIds = new Set(availableFunctionIds);
  const toolCountryIsoCodes = new Set(availableCountryIsoCodes);
  const countsHeadcount = requirements.some((requirement) => requirement.employees > 0);
  const cells = requirements.map((requirement) =>
    coverageCell(requirement, countsHeadcount, toolFunctionIds, toolCountryIsoCodes),
  );
  const possiblePoints = sumPoints(cells);
  const achievedPoints = sumPoints(cells.filter((cell) => cell.covered));
  const requiredFunctionIds = distinct(cells.map((cell) => cell.moduleFunctionId));
  const requiredCountryIsoCodes = distinct(cells.map((cell) => cell.countryIsoCode));

  return {
    score: possiblePoints === 0 ? 0 : roundToTwoDecimals((achievedPoints / possiblePoints) * 100),
    possiblePoints,
    achievedPoints,
    cells,
    gaps: cells
      .filter((cell) => !cell.covered)
      .sort((first, second) => second.points - first.points),
    requiredFunctionIds,
    coveredFunctionIds: requiredFunctionIds.filter((id) => toolFunctionIds.has(id)),
    additionalFunctionIds: [...toolFunctionIds].filter((id) => !requiredFunctionIds.includes(id)),
    requiredCountryIsoCodes,
    supportedCountryIsoCodes: requiredCountryIsoCodes.filter((isoCode) =>
      toolCountryIsoCodes.has(isoCode),
    ),
  };
}

export function functionCoverage(
  requirements: CoverageRequirementView[],
  availableFunctionIds: number[],
): number {
  const toolFunctionIds = new Set(availableFunctionIds);
  const weights = new Map(
    requirements.map((requirement) => [
      Number.parseInt(requirement.moduleFunctionId, 10),
      requirement.functionWeight,
    ]),
  );
  const requiredWeight = [...weights.values()].reduce((sum, weight) => sum + weight, 0);
  const coveredWeight = [...weights]
    .filter(([functionId]) => toolFunctionIds.has(functionId))
    .reduce((sum, [, weight]) => sum + weight, 0);

  return requiredWeight === 0 ? 0 : roundToTwoDecimals((coveredWeight / requiredWeight) * 100);
}

export function countryEmployeeCoverage(
  countryEmployees: CountryEmployeesView[],
  availableCountryIsoCodes: string[],
): number {
  const toolCountryIsoCodes = new Set(availableCountryIsoCodes);
  const countsHeadcount = countryEmployees.some((country) => country.employees > 0);
  const weight = (country: CountryEmployeesView) => (countsHeadcount ? country.employees : 1);
  const total = countryEmployees.reduce((sum, country) => sum + weight(country), 0);
  const supported = countryEmployees
    .filter((country) => toolCountryIsoCodes.has(country.countryIsoCode))
    .reduce((sum, country) => sum + weight(country), 0);

  return total === 0 ? 0 : roundToTwoDecimals((supported / total) * 100);
}

export function lostPercentagePoints(cell: CoverageCell, coverage: WeightedCoverage): number {
  return coverage.possiblePoints === 0 ? 0 : (cell.points / coverage.possiblePoints) * 100;
}

export function countryCoverage(coverage: WeightedCoverage, countryIsoCode: string): number | null {
  const countryCells = coverage.cells.filter((cell) => cell.countryIsoCode === countryIsoCode);
  const possiblePoints = sumPoints(countryCells);

  return possiblePoints === 0
    ? null
    : (sumPoints(countryCells.filter((cell) => cell.covered)) / possiblePoints) * 100;
}

export function functionSubtotals(coverage: WeightedCoverage): FunctionSubtotal[] {
  return coverage.requiredFunctionIds.map((moduleFunctionId) => {
    const functionCells = coverage.cells.filter(
      (cell) => cell.moduleFunctionId === moduleFunctionId,
    );

    return {
      moduleFunctionId,
      possiblePoints: sumPoints(functionCells),
      achievedPoints: sumPoints(functionCells.filter((cell) => cell.covered)),
    };
  });
}

export function expansionPotential(coverage: WeightedCoverage): number {
  const availableCount = coverage.coveredFunctionIds.length + coverage.additionalFunctionIds.length;

  return availableCount === 0 ? 0 : (coverage.additionalFunctionIds.length / availableCount) * 100;
}

function coverageCell(
  requirement: CoverageRequirementView,
  countsHeadcount: boolean,
  toolFunctionIds: Set<number>,
  toolCountryIsoCodes: Set<string>,
): CoverageCell {
  const moduleFunctionId = Number.parseInt(requirement.moduleFunctionId, 10);
  const functionWeight = roundToTwoDecimals(requirement.functionWeight * 100);
  const employees = countsHeadcount ? requirement.employees : 1;
  const functionAvailable = toolFunctionIds.has(moduleFunctionId);
  const countrySupported = toolCountryIsoCodes.has(requirement.countryIsoCode);

  return {
    moduleFunctionId,
    countryIsoCode: requirement.countryIsoCode,
    functionWeight,
    employees,
    points: functionWeight * employees,
    functionAvailable,
    countrySupported,
    covered: functionAvailable && countrySupported,
  };
}

function sumPoints(cells: CoverageCell[]): number {
  return cells.reduce((sum, cell) => sum + cell.points, 0);
}

function distinct<T>(values: T[]): T[] {
  return [...new Set(values)];
}

function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100;
}
